import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";
import { Icon } from "@rentiohq/web-shared/dist/components";
import { useContext, useEffect } from "react";
import {
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
} from "react-beautiful-dnd";
import {
  DragAndDropContext,
  setDragHoveredTabId,
  markTabLeft,
  setDragFinalDropTarget,
  resetDragState,
  dragHoveredTabId,
  tabLeftAt,
} from "../../context/DragAndDropContext";
import * as S from "./ApplicationTabs.styled";

export interface ITab {
  id: EIntakeStep | "archived";
  label: string;
  count: number;
  isDisabled?: boolean;
  phase?: 1 | 2 | 3 | undefined;
}

interface IProps {
  tabs: ITab[];
  activeTab: EIntakeStep | "archived";
  onTabChange: (tabId: EIntakeStep | "archived") => void;
}

export const ApplicationTabs = (props: IProps) => {
  const { tabs, activeTab, onTabChange } = props;
  const { isGlobalDragging, hoveredTab, setHoveredTab, hoveredTabRef } =
    useContext(DragAndDropContext);

  useEffect(() => {
    if (!isGlobalDragging) {
      if (hoveredTabRef.current) setHoveredTab(null);
      resetDragState();
      return;
    }

    resetDragState();

    const handleMouseMove = (e: MouseEvent) => {
      const elements = Array.from(
        document.elementsFromPoint(e.clientX, e.clientY),
      );
      const tabEl = elements.find(el => el.getAttribute("data-tab-id"));
      if (tabEl) {
        const id = tabEl.getAttribute("data-tab-id");
        if (id !== hoveredTabRef.current) setHoveredTab(id);
        setDragHoveredTabId(id);
      } else {
        if (hoveredTabRef.current !== null) setHoveredTab(null);
        if (tabLeftAt === 0 && dragHoveredTabId !== null) {
          markTabLeft();
        }
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      const elements = Array.from(
        document.elementsFromPoint(e.clientX, e.clientY),
      );
      const tabEl = elements.find(el => el.getAttribute("data-tab-id"));

      if (tabEl) {
        setDragFinalDropTarget(tabEl.getAttribute("data-tab-id"));
      } else if (
        dragHoveredTabId &&
        tabLeftAt > 0 &&
        Date.now() - tabLeftAt < 100
      ) {
        setDragFinalDropTarget(dragHoveredTabId);
      } else {
        setDragFinalDropTarget(null);
      }
    };

    window.addEventListener("mousemove", handleMouseMove, { capture: true });
    window.addEventListener("mouseup", handleMouseUp, { capture: true });
    return () => {
      window.removeEventListener("mousemove", handleMouseMove, {
        capture: true,
      });
      window.removeEventListener("mouseup", handleMouseUp, { capture: true });
    };
  }, [isGlobalDragging]);

  const regularTabs = tabs.filter(tab => tab.id !== "archived");
  const archiveTab = tabs.find(tab => tab.id === "archived");

  const phase1Tabs = regularTabs.filter(t => t.phase === 1);
  const phase2Tabs = regularTabs.filter(t => t.phase === 2);
  const phase3Tabs = regularTabs.filter(t => t.phase === 3);
  const grayTabs = regularTabs.filter(t => !t.phase);

  const renderTab = (
    tab: ITab,
    indexInGroup: number,
    globalIndex: number,
    groupSize: number,
  ) => {
    const isActive = activeTab === tab.id;
    const isArchiveTab = tab.id === "archived";
    const isDropDisabled = !!tab.isDisabled || isActive || isArchiveTab;
    const isFirstInGroup = indexInGroup === 0;
    const isLastInGroup = indexInGroup === groupSize - 1;

    return (
      <Droppable
        key={`tab-drop-${tab.id}`}
        droppableId={`tab-${tab.id}`}
        isDropDisabled={isDropDisabled}
        isCombineEnabled={false}
        ignoreContainerClipping={false}
        direction={"horizontal"}
      >
        {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              display: "flex",
              alignSelf: "stretch",
              position: "relative",
              zIndex: indexInGroup + 1,
              marginLeft: isFirstInGroup ? "0" : "-12px",
              ...(isArchiveTab ? { flex: 1 } : {}),
            }}
          >
            <S.Tab
              data-tab-id={tab.id}
              isActive={isActive}
              isFirst={isFirstInGroup}
              isLast={isLastInGroup}
              isArchive={isArchiveTab}
              phase={tab.phase}
              isDragOver={
                isGlobalDragging &&
                hoveredTab === String(tab.id) &&
                !isDropDisabled
              }
              isDropDisabled={!!tab.isDisabled}
              onClick={() => onTabChange(tab.id)}
            >
              {isArchiveTab && <Icon source="archive" size="small" />}
              <S.TabLabel data-label={tab.label}>{tab.label}</S.TabLabel>
              {tab.count > 0 && (
                <S.TabBadge isActive={isActive}>{tab.count}</S.TabBadge>
              )}
            </S.Tab>
            <div style={{ display: "none" }}>{provided.placeholder}</div>
          </div>
        )}
      </Droppable>
    );
  };

  const renderGroup = (groupTabs: ITab[], startIndex: number) => {
    if (groupTabs.length === 0) return null;
    return (
      <S.PhaseGroup key={`group-${groupTabs[0].id}`}>
        {groupTabs.map((tab, idx) =>
          renderTab(tab, idx, startIndex + idx, groupTabs.length),
        )}
      </S.PhaseGroup>
    );
  };

  return (
    <S.TabsContainer isDragging={isGlobalDragging}>
      {renderGroup(phase1Tabs, 0)}
      {renderGroup(phase2Tabs, phase1Tabs.length)}
      {renderGroup(phase3Tabs, phase1Tabs.length + phase2Tabs.length)}
      {renderGroup(
        grayTabs,
        phase1Tabs.length + phase2Tabs.length + phase3Tabs.length,
      )}
      {archiveTab && (
        <div
          key="group-archived"
          style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}
        >
          {renderTab(archiveTab, 0, regularTabs.length, 1)}
        </div>
      )}
    </S.TabsContainer>
  );
};
